import React, { useState, useEffect } from 'react';
import { ProductionLine, productionLineService } from '../services/productionLineService';

interface ProductionLineSelectorProps {
  selectedLineId: number | null;
  onLineChange: (lineId: number | null) => void;
  showAllOption?: boolean;
  showInactiveLines?: boolean;
  className?: string;
  disabled?: boolean;
  label?: string;
  excludeScheduleInactive?: boolean;
  externalLines?: ProductionLine[];
}

const ProductionLineSelector: React.FC<ProductionLineSelectorProps> = ({
  selectedLineId,
  onLineChange,
  showAllOption = true,
  showInactiveLines = false,
  className = '',
  disabled = false,
  label = 'Production Line',
  excludeScheduleInactive = false,
  externalLines,
}) => {
  const [lines, setLines] = useState<ProductionLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (externalLines) {
      setLines(externalLines);
      setLoading(false);
      return;
    }
    loadProductionLines();
  }, [showInactiveLines, excludeScheduleInactive, externalLines]);

  const loadProductionLines = async () => {
    try {
      setLoading(true);
      setError(null);

      let productionLines: ProductionLine[];
      if (showInactiveLines) {
        productionLines = await productionLineService.getAllProductionLines();
      } else {
        productionLines = await productionLineService.getActiveProductionLines(
          undefined,
          undefined,
          excludeScheduleInactive
        );
      }

      const sortedLines = productionLines.sort((a, b) => {
        if (a.displayOrder !== b.displayOrder) {
          return a.displayOrder - b.displayOrder;
        }
        return a.name.localeCompare(b.name);
      });

      setLines(sortedLines);
    } catch (err) {
      console.error('Error loading production lines:', err);
      setError('Failed to load production lines');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    const lineId = value === '' ? null : parseInt(value, 10);
    onLineChange(lineId);
  };

  const getSelectedValue = () => {
    return selectedLineId === null ? '' : selectedLineId.toString();
  };

  const getLineDisplayName = (line: ProductionLine) => {
    const typeLabel = line.lineType === 'machine' ? 'Machine' : 'Manual';
    const workerInfo = ` (${typeLabel}, ${line.defaultWorkers} workers)`;
    const statusInfo = !line.isActive ? ' [INACTIVE]' : '';
    return `${line.name}${workerInfo}${statusInfo}`;
  };

  if (loading) {
    return (
      <div className={`production-line-selector ${className}`}>
        <label className="form-label">{label}</label>
        <select className="form-select" disabled>
          <option>Loading...</option>
        </select>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`production-line-selector ${className}`}>
        <label className="form-label">{label}</label>
        <select className="form-select" disabled>
          <option>Error loading lines</option>
        </select>
        <div className="text-danger small mt-1">{error}</div>
      </div>
    );
  }

  return (
    <div className={`production-line-selector ${className}`}>
      <label className="form-label">{label}</label>
      <select
        className="form-select"
        value={getSelectedValue()}
        onChange={handleChange}
        disabled={disabled}
      >
        {showAllOption && <option value="">All</option>}
        {lines.map((line) => (
          <option key={line.id} value={line.id} disabled={!line.isActive && !showInactiveLines}>
            {getLineDisplayName(line)}
          </option>
        ))}
        {lines.length === 0 && (
          <option value="" disabled>
            Loading...
          </option>
        )}
      </select>
    </div>
  );
};

export default ProductionLineSelector;
